import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { toastError } from './toast'

// Create axios instance
const request: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Token刷新状态追踪
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

// 刷新Token函数
const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    return false
  }

  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )

    const data = response.data
    if (data.code === 0 && data.data?.access_token) {
      localStorage.setItem('access_token', data.data.access_token)
      localStorage.setItem('refresh_token', data.data.refresh_token)
      return true
    }
    return false
  } catch (err) {
    console.error('Token refresh failed:', err)
    return false
  }
}

// Request interceptor
request.interceptors.request.use(
  (config) => {
    // Add token from localStorage
    const token = localStorage.getItem('access_token')
    console.log('Request:', config.method?.toUpperCase(), config.url, 'data:', config.data)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // Ensure data is properly serialized for POST/PUT/DELETE
    if (config.method === 'post' || config.method === 'put' || config.method === 'delete') {
      if (config.data && typeof config.data === 'object' && !(config.data instanceof String)) {
        config.data = JSON.stringify(config.data)
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data
    console.log('Response received:', response.config.url, 'full response:', res)
    console.log('Response code:', res.code, 'message:', res.message)
    
    // Handle custom error codes - only check if code exists
    if (res.code !== undefined && res.code !== 0 && res.code !== 200) {
      console.log('Error response:', res)
      toastError(res.message || 'Request failed')
      return Promise.reject(new Error(res.message || 'Request failed'))
    }
    
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle network errors
    if (error.response) {
      // 如果是401错误且不是刷新token的请求，尝试刷新token
      if (error.response.status === 401 && !originalRequest._retry) {
        // 开发模式下也尝试刷新token
        // 防止重复刷新
        if (isRefreshing) {
          if (refreshPromise) {
            const success = await refreshPromise
            if (success) {
              originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`
              return request(originalRequest)
            }
          }
          // 刷新失败，清除token
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          if (!import.meta.env.DEV) {
            window.location.href = '/login'
          }
          return Promise.reject(error)
        }

        isRefreshing = true
        originalRequest._retry = true

        refreshPromise = refreshAccessToken()
        const success = await refreshPromise
        isRefreshing = false
        refreshPromise = null

        if (success) {
          // 刷新成功，重试原请求
          originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`
          return request(originalRequest)
        }
      }

      // 非401错误或刷新失败
      switch (error.response.status) {
        case 401:
          // 开发模式下不自动跳转登录页
          if (!import.meta.env.DEV) {
            toastError('Unauthorized, please login again')
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            window.location.href = '/login'
          }
          break
        case 403:
          toastError('Forbidden')
          break
        case 404:
          toastError('Resource not found')
          break
        case 500:
          toastError('Server error')
          break
        default:
          toastError(error.response.data?.message || 'Request failed')
      }
    } else if (error.request) {
      toastError('Network error, please check your connection')
    } else {
      toastError(error.message || 'Request failed')
    }
    return Promise.reject(error)
  }
)

export default request
